import { ReactNode, useState } from "react";
import { AdminSidebar } from "./AdminSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AdminRoute } from "./AdminRoute";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileNavBar } from "@/components/dashboard/MobileNavBar";
import { AdminErrorBoundary } from "./AdminErrorBoundary";
import { 
  Home, 
  LayoutDashboard, 
  Users, 
  MoreHorizontal,
  Activity,
  Heart,
  Bell,
  Key,
  Shield,
  Database,
  DollarSign,
  BarChart3,
  MessageSquare,
  Gift,
  Ticket,
  ChevronRight
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useHapticButton } from "@/hooks/useHapticButton";

interface AdminLayoutProps {
  children: ReactNode;
}

const moreNavGroups = [
  {
    label: "Core",
    items: [
      { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
      { icon: Users, label: "Users", path: "/admin/users" },
      { icon: DollarSign, label: "Revenue", path: "/admin/revenue" },
    ]
  },
  {
    label: "Monitoring",
    items: [
      { icon: Activity, label: "Monitoring", path: "/admin/monitoring" },
      { icon: Shield, label: "Security Audit", path: "/admin/security" },
      { icon: Database, label: "Data Retention", path: "/admin/data-retention" },
      { icon: Heart, label: "System Health", path: "/admin/health" },
      { icon: Heart, label: "Customer Health", path: "/admin/customer-health" },
    ]
  },
  {
    label: "Platform",
    items: [
      { icon: BarChart3, label: "Analytics", path: "/admin/analytics" },
      { icon: MessageSquare, label: "Platform Feedback", path: "/admin/feedback" },
      { icon: Bell, label: "Notifications", path: "/admin/notifications" },
    ]
  },
  {
    label: "Features",
    items: [
      { icon: Key, label: "API Keys", path: "/admin/api-keys" },
      { icon: Gift, label: "Referrals", path: "/admin/referrals" },
    ]
  },
  {
    label: "Customer Success",
    items: [
      { icon: Ticket, label: "Support Tickets", path: "/admin/support" },
    ]
  }
];

// Admin-specific mobile bottom nav items
function AdminMobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const triggerHaptic = useHapticButton('selection');
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);

  const navItems = [
    { icon: Home, label: "App", path: "/dashboard" },
    { icon: LayoutDashboard, label: "Overview", path: "/admin/dashboard" },
    { icon: Heart, label: "Health", path: "/admin/health" },
    { icon: MoreHorizontal, label: "More", path: null },
  ];

  const handleNavClick = (path: string | null) => {
    triggerHaptic();
    if (path === null) {
      setMoreSheetOpen(true);
    } else {
      navigate(path);
    }
  };

  const handleMoreNavClick = (path: string) => {
    triggerHaptic();
    setMoreSheetOpen(false);
    navigate(path);
  };

  const isActive = (path: string | null) => {
    if (path === null) return false;
    if (path === "/admin/dashboard") {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <nav 
        className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border md:hidden"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div className="flex items-center justify-around h-16">
          {navItems.map((item, index) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            return (
              <button
                key={item.path || `more-${index}`}
                onClick={() => handleNavClick(item.path)}
                className={cn(
                  "flex flex-col items-center justify-center gap-1 flex-1 h-full relative",
                  "transition-colors duration-200 active:scale-95",
                  active ? "text-primary" : "text-muted-foreground"
                )}
              >
                <Icon className={cn("h-5 w-5", active && "animate-scale-in")} />
                <span className={cn(
                  "text-[10px] font-medium transition-all duration-200",
                  active && "font-semibold"
                )}>
                  {item.label}
                </span>
                {active && (
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-primary rounded-full" />
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* More Navigation Sheet */}
      <Sheet open={moreSheetOpen} onOpenChange={setMoreSheetOpen}>
        <SheetContent side="bottom" className="h-[70vh] rounded-t-[20px] p-0">
          <div className="p-4 border-b border-border">
            <h2 className="text-lg font-semibold">Admin Navigation</h2>
            <p className="text-sm text-muted-foreground">Access all admin features</p>
          </div>
          <div className="overflow-y-auto h-[calc(70vh-80px)] p-4 space-y-6">
            {moreNavGroups.map((group) => (
              <div key={group.label}>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  {group.label}
                </h3>
                <div className="space-y-1">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = location.pathname === item.path;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleMoreNavClick(item.path)}
                        className={cn(
                          "flex items-center justify-between w-full p-3 rounded-lg transition-colors",
                          active 
                            ? "bg-primary/10 text-primary" 
                            : "hover:bg-muted"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="h-5 w-5" />
                          <span className="font-medium">{item.label}</span>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  return (
    <AdminRoute>
      <SidebarProvider defaultOpen={!isMobile}>
        <div className="flex min-h-screen w-full bg-background">
          {/* Mobile Navigation */}
          {isMobile && (
            <>
              <MobileNavBar onMenuClick={() => setMobileMenuOpen(true)} />
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetContent side="left" className="p-0 w-[280px]">
                  <AdminSidebar />
                </SheetContent>
              </Sheet>
            </>
          )}

          {/* Desktop Sidebar */}
          {!isMobile && <AdminSidebar />}
          
          {/* Main Content */}
          <main className="flex-1 pt-16 pb-20 md:pt-0 md:pb-0">
            <AdminErrorBoundary>
              {children}
            </AdminErrorBoundary>
          </main>

          {/* Mobile Bottom Navigation */}
          {isMobile && <AdminMobileBottomNav />}
        </div>
      </SidebarProvider>
    </AdminRoute>
  );
}
